import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Loader2, MapPin, IndianRupee, Ruler } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../AuthContext";

const STATUSES = [
  { key: "Prospecting", tint: "border-t-[#9ca3af]" },
  { key: "Diligence", tint: "border-t-[#60a5fa]" },
  { key: "Aggregation", tint: "border-t-[#a78bfa]" },
  { key: "Legal", tint: "border-t-[#f59e0b]" },
  { key: "Delivered", tint: "border-t-[#34d399]" },
  { key: "On Hold", tint: "border-t-[#ef4444]" },
];

const CLIENT_TYPES = ["Government", "MNC", "Private"];

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      if (isAdmin) {
        const { data: users } = await api.get("/users");
        setEmployees(users);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(STATUSES.map((s) => [s.key, []]));
    projects.forEach((p) => {
      if (map[p.status]) map[p.status].push(p);
      else map["Prospecting"].push(p);
    });
    return map;
  }, [projects]);

  const changeStatus = async (project, newStatus) => {
    if (project.status === newStatus) return;
    try {
      await api.patch(`/projects/${project.id}`, { status: newStatus });
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? { ...p, status: newStatus } : p))
      );
      toast.success(`Moved to ${newStatus}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="overline mb-3">Operations Board</div>
          <h1 className="font-display text-4xl md:text-5xl leading-none text-neutral-50">
            Projects <span className="italic text-[#D4AF37]">Pipeline</span>
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">
            {projects.length} project{projects.length !== 1 && "s"} · Karnataka
            {!isAdmin && " · showing your assignments"}
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="new-project-btn"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#08080A] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B]"
          >
            <Plus className="w-4 h-4" /> New Project
          </button>
        )}
      </header>

      {loading ? (
        <div className="flex items-center gap-3 text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading pipeline…
        </div>
      ) : projects.length === 0 ? (
        <EmptyState isAdmin={isAdmin} onNew={() => setShowForm(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
          {STATUSES.map((s) => (
            <Column
              key={s.key}
              status={s.key}
              tint={s.tint}
              projects={byStatus[s.key]}
              onChangeStatus={changeStatus}
            />
          ))}
        </div>
      )}

      {showForm && (
        <NewProjectDialog
          employees={employees}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Column({ status, tint, projects, onChangeStatus }) {
  return (
    <div
      data-testid={`column-${status.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-[#0C0C10] border-t-2 ${tint} border-white/5 p-4 min-h-[400px]`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs tracking-[0.24em] uppercase text-neutral-300">
          {status}
        </div>
        <div className="text-[0.65rem] tracking-widest uppercase text-neutral-500 border border-white/10 px-2 py-0.5">
          {projects.length}
        </div>
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} onChangeStatus={onChangeStatus} />
        ))}
        {projects.length === 0 && (
          <div className="text-xs text-neutral-600 italic py-6 text-center">— empty —</div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ project, onChangeStatus }) {
  return (
    <Link
      to={`/portal/projects/${project.id}`}
      data-testid={`project-card-${project.id}`}
      className="block bg-[#121215] border border-white/8 p-4 hover:border-[#D4AF37]/60 transition-colors group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[0.6rem] tracking-widest uppercase text-[#D4AF37]">
          {project.code}
        </div>
        <span className="text-[0.55rem] tracking-widest uppercase text-neutral-500 border border-white/10 px-1.5 py-0.5">
          {project.client_type}
        </span>
      </div>
      <div className="font-display text-lg text-neutral-50 leading-tight mb-2 group-hover:text-[#D4AF37] transition-colors">
        {project.name}
      </div>
      <div className="text-xs text-neutral-400 mb-3 truncate">{project.client}</div>

      <div className="flex items-center gap-3 text-[0.68rem] text-neutral-500 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {project.district}
        </span>
        {project.area_acres > 0 && (
          <span className="inline-flex items-center gap-1">
            <Ruler className="w-3 h-3" />
            {project.area_acres} ac
          </span>
        )}
        {project.deal_value_inr > 0 && (
          <span className="inline-flex items-center gap-1">
            <IndianRupee className="w-3 h-3" />
            {formatINR(project.deal_value_inr)}
          </span>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-white/5">
        <select
          data-testid={`status-select-${project.id}`}
          value={project.status}
          onClick={(e) => e.preventDefault()}
          onChange={(e) => {
            e.preventDefault();
            onChangeStatus(project, e.target.value);
          }}
          className="w-full bg-[#08080A] border border-white/10 text-xs text-neutral-300 py-1.5 px-2 focus:outline-none focus:border-[#D4AF37]"
        >
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.key}
            </option>
          ))}
        </select>
      </div>
    </Link>
  );
}

function EmptyState({ isAdmin, onNew }) {
  return (
    <div className="border border-white/8 bg-[#0C0C10] p-16 text-center">
      <div className="font-display text-3xl text-neutral-100 mb-3">The board is empty.</div>
      <p className="text-neutral-500 max-w-md mx-auto mb-6">
        {isAdmin
          ? "Create your first project to see it appear on the pipeline board."
          : "You have not been assigned to any projects yet. Please contact your administrator."}
      </p>
      {isAdmin && (
        <button
          data-testid="empty-new-project-btn"
          onClick={onNew}
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#08080A] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B]"
        >
          <Plus className="w-4 h-4" /> New Project
        </button>
      )}
    </div>
  );
}

function NewProjectDialog({ employees, onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    client: "",
    client_type: "Government",
    district: "",
    taluk: "",
    area_acres: "",
    deal_value_inr: "",
    status: "Prospecting",
    assigned_employee_ids: [],
    start_date: "",
    expected_close_date: "",
  });
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleEmployee = (id) => {
    setForm((f) => ({
      ...f,
      assigned_employee_ids: f.assigned_employee_ids.includes(id)
        ? f.assigned_employee_ids.filter((x) => x !== id)
        : [...f.assigned_employee_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        area_acres: Number(form.area_acres || 0),
        deal_value_inr: Number(form.deal_value_inr || 0),
        start_date: form.start_date || null,
        expected_close_date: form.expected_close_date || null,
        taluk: form.taluk || null,
      };
      await api.post("/projects", payload);
      toast.success("Project created.");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="new-project-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-3xl bg-[#0C0C10] border border-white/10 my-8"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/8">
          <div>
            <div className="overline">New Project</div>
            <div className="font-display text-2xl text-neutral-50 mt-1">
              Create a pipeline entry
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-100"
          >
            <X />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label="Project Code *" testId="np-code">
            <input required value={form.code} onChange={change("code")} className={inpCls} placeholder="RE-KA-042" />
          </FormField>
          <FormField label="Project Name *" testId="np-name">
            <input required value={form.name} onChange={change("name")} className={inpCls} placeholder="Devanahalli Aggregation" />
          </FormField>
          <FormField label="Client *" testId="np-client">
            <input required value={form.client} onChange={change("client")} className={inpCls} placeholder="e.g., Karnataka Industrial Areas Development Board" />
          </FormField>
          <FormField label="Client Type" testId="np-client-type">
            <select value={form.client_type} onChange={change("client_type")} className={inpCls}>
              {CLIENT_TYPES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </FormField>
          <FormField label="District *" testId="np-district">
            <input required value={form.district} onChange={change("district")} className={inpCls} placeholder="Bengaluru Rural" />
          </FormField>
          <FormField label="Taluk" testId="np-taluk">
            <input value={form.taluk} onChange={change("taluk")} className={inpCls} placeholder="Devanahalli" />
          </FormField>
          <FormField label="Area (acres)" testId="np-area">
            <input type="number" step="0.01" min="0" value={form.area_acres} onChange={change("area_acres")} className={inpCls} placeholder="42.5" />
          </FormField>
          <FormField label="Deal Value (₹)" testId="np-value">
            <input type="number" step="1" min="0" value={form.deal_value_inr} onChange={change("deal_value_inr")} className={inpCls} placeholder="120000000" />
          </FormField>
          <FormField label="Start Date" testId="np-start">
            <input type="date" value={form.start_date} onChange={change("start_date")} className={inpCls} />
          </FormField>
          <FormField label="Expected Close Date" testId="np-close">
            <input type="date" value={form.expected_close_date} onChange={change("expected_close_date")} className={inpCls} />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Initial Status" testId="np-status">
              <select value={form.status} onChange={change("status")} className={inpCls}>
                {STATUSES.map((s) => <option key={s.key}>{s.key}</option>)}
              </select>
            </FormField>
          </div>

          <div className="md:col-span-2">
            <label className="overline block mb-3">Assign Employees</label>
            {employees.length === 0 ? (
              <div className="text-xs text-neutral-500 italic">
                No employees yet. Add employees in the Team section.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {employees.map((emp) => {
                  const active = form.assigned_employee_ids.includes(emp.id);
                  return (
                    <button
                      type="button"
                      key={emp.id}
                      data-testid={`assign-${emp.id}`}
                      onClick={() => toggleEmployee(emp.id)}
                      className={`px-3 py-1.5 border text-[0.7rem] tracking-widest uppercase transition-colors ${
                        active
                          ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                          : "border-white/15 text-neutral-400 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      {emp.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-white/8 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-xs tracking-[0.24em] uppercase text-neutral-400 hover:text-neutral-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="np-submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#08080A] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create Project
          </button>
        </div>
      </form>
    </div>
  );
}

const inpCls =
  "w-full bg-[#08080A] border border-white/10 py-2.5 px-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D4AF37]";

function FormField({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="overline block mb-2">{label}</label>
      {children}
    </div>
  );
}

function formatINR(n) {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}
